import { useState, useEffect } from "react";
import { Link, useLocation, useRoute } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { queryClient } from "@/lib/queryClient";
import { Building2, Mail, Lock, ArrowRight, Users, Shield, UserPlus, Eye, EyeOff } from "lucide-react";
import propellerLogo from "@assets/propeller-logo.png";
import { API_CONFIG } from "@/config/environment";
import { supabase } from "@/lib/supabase";

const loginSchema = z.object({
  username: z.string().min(1, "Please enter your email or username"),
  password: z.string().min(1, "Please enter your password"),
});

type LoginForm = z.infer<typeof loginSchema>;

const enterpriseSchema = z.object({
  domain: z.string().min(1, "Please enter your organization domain"),
});

const registrationSchema = z.object({
  username: z.string().min(3, "Username must be at least 3 characters"),
  email: z.string().email("Please enter a valid email address"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  organization: z.string().optional(),
  role: z.string().optional(),
  reason: z.string().min(10, "Please provide a reason for your request (minimum 10 characters)"),
});

type EnterpriseForm = z.infer<typeof enterpriseSchema>;
type RegistrationForm = z.infer<typeof registrationSchema>;

export default function Login() {
  const [, setLocation] = useLocation();
  const [loginType, setLoginType] = useState<'standard' | 'enterprise'>('standard');
  const [showRegistration, setShowRegistration] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [resetStep, setResetStep] = useState<'choice' | 'email' | 'verify' | 'newPassword' | 'complete'>('choice');
  const [resetType, setResetType] = useState<'username' | 'password'>('username');
  const [resetMessage, setResetMessage] = useState('');
  const [recoveredUsername, setRecoveredUsername] = useState('');
  const [resetData, setResetData] = useState({
    email: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { toast } = useToast();
  
  // Check for registration success parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('registration') === 'success') {
      toast({
        title: "Registration Successful! 🎉",
        description: "Your account has been created successfully. Please sign in with your credentials to begin your Red Helicopter journey.",
        duration: 8000, // Show for 8 seconds
      });
      // Clean up the URL parameter
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [toast]);

  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const enterpriseForm = useForm<EnterpriseForm>({
    resolver: zodResolver(enterpriseSchema),
    defaultValues: {
      domain: "",
    },
  });

  const registrationForm = useForm<RegistrationForm>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      username: "",
      email: "",
      firstName: "",
      lastName: "",
      organization: "",
      role: "",
      reason: "",
    },
  });

  const handleStandardLogin = async (data: LoginForm) => {
    setIsLoading(true);
    setLoginAttempts(prev => prev + 1);
    try {
      const email = data.username; // Accept email in username field for now
      const password = data.password;

      console.log('[CLIENT] Logging in with Supabase:', email);

      // Sign in with Supabase
      const { data: authData, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('[CLIENT] Login error:', error.message);

        // Show cache refresh hint only after 2 failed attempts
        const showCacheHint = loginAttempts >= 2;

        toast({
          title: "Login Failed",
          description: showCacheHint
            ? error.message + '. If you\'re certain your credentials are correct, try refreshing the page (Ctrl+F5 or Cmd+Shift+R).'
            : error.message,
          variant: "destructive",
          duration: showCacheHint ? 8000 : 4000,
        });
        return;
      }

      if (!authData.session) {
        toast({
          title: "Login Failed",
          description: "No session returned. Please try again.",
          variant: "destructive",
        });
        return;
      }

      console.log('[CLIENT] Login successful');

      // Store session in localStorage
      localStorage.setItem('supabase_session', JSON.stringify(authData.session));
      localStorage.setItem('user', JSON.stringify(authData.user));

      // Set login success flag for authenticated users
      sessionStorage.setItem('loginSuccess', 'true');

      // Clear any existing auth cache and update with new user data
      await queryClient.invalidateQueries({ queryKey: ["auth/user"] });
      queryClient.setQueryData(["auth/user"], authData.user);

      // Check for redirect parameter from URL (e.g., from scenarios)
      const urlParams = new URLSearchParams(window.location.search);
      const redirectParam = urlParams.get('redirect');

      let finalRedirectUrl = '/';
      if (redirectParam === 'scenarios') {
        finalRedirectUrl = '/scenarios';
      }

      // For admin routes, we need a full page reload to ensure proper session handling
      if (finalRedirectUrl.includes('/admin')) {
        toast({
          title: "Login Successful",
          description: "Redirecting to admin dashboard...",
        });

        setTimeout(() => {
          window.location.href = finalRedirectUrl;
        }, 500);
      } else if (redirectParam === 'scenarios') {
        toast({
          title: "Login Successful",
          description: "Redirecting to Money Scenarios...",
        });
        setTimeout(() => {
          window.location.href = finalRedirectUrl;
        }, 500);
      } else {
        toast({
          title: "Login Successful",
          description: "Welcome to your Red Helicopter journey!",
          duration: 2000,
        });

        // Use client-side navigation
        setTimeout(() => {
          setLocation(finalRedirectUrl);
        }, 100);
      }
    } catch (error) {
      console.error('[CLIENT] Login exception:', error);
      toast({
        title: "Login Error",
        description: 'Network error: ' + ((error as any)?.message || 'Unable to connect to server'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnterpriseSSO = async (data: EnterpriseForm) => {
    setIsLoading(true);
    try {
      // Redirect to SSO provider based on domain
      window.location.href = '/api/auth/sso?domain=' + encodeURIComponent(data.domain);
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleRegistration = async (data: RegistrationForm) => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Registration Submitted",
          description: "Your registration request has been submitted and is pending admin approval. You will be notified once approved.",
        });
        setShowRegistration(false);
        registrationForm.reset();
      } else {
        const error = await response.json();
        toast({
          title: "Registration Failed",
          description: error.message || "Failed to submit registration request",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Registration Error",
        description: "An error occurred during registration. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoverUsername = async () => {
    setIsLoading(true);
    setResetMessage('');
    
    try {
      const response = await fetch('/api/auth/recover-username', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetData.email }),
      });

      const result = await response.json();

      if (response.ok) {
        setRecoveredUsername(result.username);
        setResetMessage('Your username is: ' + result.username + '. You can now sign in.');
        setResetStep('complete');
        toast({
          title: "Username Found",
          description: 'Your username is: ' + result.username,
        });
      } else {
        setResetMessage(result.message || 'Email not found');
        toast({
          title: "Username Recovery Failed",
          description: result.message || "Email not found in our records.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setResetMessage('An error occurred. Please try again.');
      toast({
        title: "Error",
        description: "An error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordResetRequest = async () => {
    setIsLoading(true);
    setResetMessage('');
    
    try {
      const response = await fetch('/api/auth/reset-password-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetData.email }),
      });

      const result = await response.json();

      if (response.ok) {
        setResetMessage('Account verified for ' + result.firstName + '. You can now set a new password.');
        setResetStep('newPassword');
        toast({
          title: "Account Verified",
          description: "You can now set a new password.",
        });
      } else {
        setResetMessage(result.message || 'Email not found');
        toast({
          title: "Account Verification Failed",
          description: result.message || "Email not found in our records.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setResetMessage('An error occurred. Please try again.');
      toast({
        title: "Error",
        description: "An error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordResetUpdate = async () => {
    if (resetData.newPassword !== resetData.confirmPassword) {
      setResetMessage('Passwords do not match');
      toast({
        title: "Password Mismatch",
        description: "Passwords do not match. Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (resetData.newPassword.length < 6) {
      setResetMessage('Password must be at least 6 characters long');
      toast({
        title: "Password Too Short",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setResetMessage('');
    
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: resetData.email,
          newPassword: resetData.newPassword,
          confirmPassword: resetData.confirmPassword
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setResetMessage('Password updated successfully for username: ' + result.username + '. You can now sign in.');
        setResetStep('complete');
        toast({
          title: "Password Updated",
          description: "Your password has been updated successfully. You can now sign in.",
        });
      } else {
        setResetMessage(result.message || 'Failed to update password');
        toast({
          title: "Password Update Failed",
          description: result.message || "Failed to update password.",
          variant: "destructive",
        });
      }
    } catch (error) {
      setResetMessage('An error occurred. Please try again.');
      toast({
        title: "Error",
        description: "An error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-screen bg-gradient-to-br from-rh-calm via-white to-rh-trust-blue/10 flex items-center justify-center p-4 overflow-hidden">
      <div className="w-full max-w-4xl grid lg:grid-cols-2 gap-6 items-center h-full">
        
        {/* Left side - Branding */}
        <div className="text-center flex flex-col justify-center items-center h-full">
          <div className="space-y-6">
            {/* Propeller logo */}
            <div className="w-32 h-32 mx-auto">
              <img 
                src={propellerLogo}
                alt="Red Helicopter Propeller"
                className="w-full h-full object-contain"
              />
            </div>
            
            {/* Feature list - only show for Enterprise */}
            {loginType === 'enterprise' && (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 text-gray-700 text-sm">
                  <Users className="h-4 w-4 text-rh-trust-blue" />
                  <span>Enterprise-ready with SSO integration</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-gray-700 text-sm">
                  <Shield className="h-4 w-4 text-rh-growth-green" />
                  <span>Secure, compliant, and scalable</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-gray-700 text-sm">
                  <Building2 className="h-4 w-4 text-rh-warmth-orange" />
                  <span>Trusted by leading organizations</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right side - Login Forms */}
        <div className="flex flex-col justify-center space-y-4">
          <Card className="w-full max-w-md mx-auto">
            <CardHeader className="space-y-1 pb-4">
              <CardDescription className="text-center text-sm">
                Choose your access method below
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
            
            {/* Login Type Selector */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={loginType === 'standard' ? 'default' : 'outline'}
                onClick={() => setLoginType('standard')}
                className="w-full"
              >
                <Mail className="mr-2 h-4 w-4" />
                Standard
              </Button>
              <Button
                variant={loginType === 'enterprise' ? 'default' : 'outline'}
                onClick={() => setLoginType('enterprise')}
                className="w-full"
              >
                <Building2 className="mr-2 h-4 w-4" />
                Enterprise
              </Button>
            </div>

            {loginType === 'standard' ? (
              <div className="space-y-4">
                <Form {...loginForm}>
                  <form onSubmit={loginForm.handleSubmit(handleStandardLogin)} className="space-y-4">
                    <FormField
                      control={loginForm.control}
                      name="username"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="your@email.com"
                              type="email"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={loginForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                placeholder="Password"
                                type={showPassword ? "text" : "password"}
                                {...field}
                                className="pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                                tabIndex={-1}
                              >
                                {showPassword ? (
                                  <EyeOff className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <Eye className="h-4 w-4 text-gray-500" />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button 
                      type="submit" 
                      className="w-full journey-button"
                      disabled={isLoading}
                    >
                      {isLoading ? "Signing in..." : "Sign In"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </Form>
                
                {/* Reset Username/Password Link */}
                <div className="text-center">
                  <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600 mb-2">Need help accessing your account?</p>
                    <button
                      onClick={() => setShowPasswordReset(true)}
                      className="text-sm text-rh-radiant-red hover:text-rh-helicopter-red underline hover:no-underline transition-colors"
                    >
                      Reset username/password →
                    </button>
                  </div>
                </div>
                
                {/* Removed social login options - only username/password authentication is available */}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center text-sm text-gray-600 mb-4">
                  For testing: Use Standard login with admin credentials, or enter your organization domain for SSO
                </div>
                
                <Form {...enterpriseForm}>
                  <form onSubmit={enterpriseForm.handleSubmit(handleEnterpriseSSO)} className="space-y-4">
                    <FormField
                      control={enterpriseForm.control}
                      name="domain"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Organization Domain</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="your-organization.com"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button 
                      type="submit" 
                      className="w-full journey-button"
                      disabled={isLoading}
                    >
                      {isLoading ? "Connecting..." : "Continue with SSO"}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </Form>
              </div>
            )}



            <div className="text-center space-y-2">
              <p className="text-sm text-gray-600">
                Don't have an account?{" "}
                <Link href="/register" className="text-rh-helicopter-red hover:underline">
                  Sign up
                </Link>
              </p>
              <div className="text-xs text-gray-500">
                <p>Enterprise SSO Administrators: Configure SAML 2.0 with callback URL: https://app.redheli.com/auth/callback</p>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      </div>

      {/* Reset Password Modal */}
      {showPasswordReset && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Reset Your Credentials</h3>
              <button
                onClick={() => {
                  setShowPasswordReset(false);
                  setResetStep('choice');
                  setResetMessage('');
                  setResetData({ email: '', newPassword: '', confirmPassword: '' });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {resetStep === 'choice' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">What would you like to recover?</p>
                
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setResetType('username');
                      setResetStep('email');
                    }}
                    className="w-full p-3 text-left border border-gray-200 rounded-lg hover:border-rh-radiant-red hover:bg-red-50 transition-colors"
                  >
                    <div className="font-medium text-sm">Recover Username</div>
                    <div className="text-xs text-gray-500">I forgot my username</div>
                  </button>
                  
                  <button
                    onClick={() => {
                      setResetType('password');
                      setResetStep('email');
                    }}
                    className="w-full p-3 text-left border border-gray-200 rounded-lg hover:border-rh-radiant-red hover:bg-red-50 transition-colors"
                  >
                    <div className="font-medium text-sm">Reset Password</div>
                    <div className="text-xs text-gray-500">I forgot my password</div>
                  </button>
                </div>
              </div>
            )}

            {resetStep === 'email' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Enter the email address associated with your account:
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={resetData.email}
                    onChange={(e) => setResetData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full"
                  />
                </div>

                {resetMessage && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">{resetMessage}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setResetStep('choice')}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={resetType === 'username' ? handleRecoverUsername : handlePasswordResetRequest}
                    disabled={isLoading || !resetData.email}
                    className="flex-1"
                  >
                    {isLoading ? "Processing..." : resetType === 'username' ? "Get Username" : "Verify Account"}
                  </Button>
                </div>
              </div>
            )}

            {resetStep === 'newPassword' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Create a new password for your account:
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={resetData.newPassword}
                      onChange={(e) => setResetData(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="w-full pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={resetData.confirmPassword}
                      onChange={(e) => setResetData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="w-full pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                </div>

                {resetMessage && (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">{resetMessage}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setResetStep('email')}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handlePasswordResetUpdate}
                    disabled={isLoading || !resetData.newPassword || !resetData.confirmPassword}
                    className="flex-1"
                  >
                    {isLoading ? "Updating..." : "Update Password"}
                  </Button>
                </div>
              </div>
            )}

            {resetStep === 'complete' && (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-green-600 text-xl">✓</span>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">Success!</h4>
                </div>

                {resetMessage && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">{resetMessage}</p>
                  </div>
                )}

                <Button
                  onClick={() => {
                    setShowPasswordReset(false);
                    setResetStep('choice');
                    setResetMessage('');
                    setResetData({ email: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="w-full"
                >
                  Continue to Sign In
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}